import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  model,
  signal,
  Input,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  effect,
} from '@angular/core';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  AutocompleteService,
  PaginationResponse,
} from '../../services/autocomplete.service';
import { Observable } from 'rxjs';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


import { MatAutocompleteInfiniteScrollDirective } from '../../directives/mat-autocoplete-infinite-scroll.directive';

interface Item {
  id: number | string;
  name: string;
}

@Component({
  selector: 'app-autocomplete-list',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatAutocompleteInfiniteScrollDirective
],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteListComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './autocomplete-list.component.html',
})
export class AutocompleteListComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly currentItem = model('');
  readonly items = signal<Item[]>([]);
  readonly allItems = signal<Item[]>([]);

  readonly filteredItems = computed(() => {
    return this.allItems();
  });

  @Input() fetchFn!: (params: any) => Observable<PaginationResponse<Item>>;
  @Input() label: string = '';
  @Input() additionalParams: any = {};

  private additionalParamsSignal = signal<any>({});
  pagination = new AutocompleteService<Item>();
  readonly announcer = inject(LiveAnnouncer);
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  private onChange = (value: Item[]) => {};
  onTouched = () => {};
  disabled = false;

  constructor() {
    effect(
      () => {
        const searchTerm = this.currentItem();
        this.searchItems(searchTerm);
      }
    );

    effect(
      () => {
        const params = this.additionalParamsSignal();
        if (Object.keys(params).length > 0) {
          this.reloadData();
        }
      }
    );
  }

  openOnFocus() {
    if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
      this.autocompleteTrigger.openPanel();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['additionalParams'] &&
      !changes['additionalParams'].firstChange
    ) {
      this.additionalParamsSignal.set(this.additionalParams);
    }
  }

  ngOnInit(): void {
    this.additionalParamsSignal.set(this.additionalParams);

    if (!this.fetchFn) {
      throw new Error('fetchFn is required for app-autocomplete-list');
    }
    this.pagination.reset();
    this.pagination.init((params) =>
      this.fetchFn({ ...params, ...this.additionalParams })
    );
    this.pagination.loadFirstPage();
    this.pagination.items$.subscribe((list) => {
      this.allItems.set(list ?? []);
    });
  }

  private reloadData(): void {
    if (this.fetchFn) {
      this.pagination.reset();
      this.pagination.init((params) =>
        this.fetchFn({ ...params, ...this.additionalParams })
      );
      this.pagination.loadFirstPage();
    }
  }

  private searchItems(searchTerm: string): void {
    setTimeout(() => {
      if (this.currentItem() === searchTerm) {
        this.pagination.reset();
        this.pagination.loadFirstPage(searchTerm);
      }
    }, 300);
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    if (value) {
      const newItem: Item = { id: value, name: value };
      this.items.update((items) => [...items, newItem]);
      this.emitChange();
    }
    this.currentItem.set('');
  }

  remove(item: Item): void {
    this.items.update((items) => {
      const index = items.findIndex((f) => f.id === item.id);
      if (index < 0) return items;

      const updated = [...items];
      updated.splice(index, 1);
      this.announcer.announce(`Removed ${item.name}`);
      return updated;
    });
    this.emitChange();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedItem: Item = event.option.value;
    this.items.update((items) => {
      if (!items.some((f) => f.id === selectedItem.id)) {
        return [...items, selectedItem];
      }
      return items;
    });
    this.currentItem.set('');
    event.option.deselect();
    this.emitChange();
  }

  writeValue(value: Item[]): void {
    if (value) {
      this.items.set(value);
    }
  }

  registerOnChange(fn: (value: Item[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private emitChange(): void {
    this.onChange(this.items());
    this.onTouched();
  }
}
